import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import {
  SESSION_STORAGE_AUTH_RETURN_TO,
  SESSION_STORAGE_LOGOUT_INITIATED,
} from '../../constants/storage-key';

export const AuthViewLogout = () => {
  useEffect(() => {
    sessionStorage.setItem(SESSION_STORAGE_LOGOUT_INITIATED, 'true');
    StandaloneAuth.client.logout({ returnTo: window.location.origin });
  }, []);

  return <LoadingIndicator />;
};

export const AuthViewLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Handle Auth0 callbacks when the tenant's redirect URI points to /sign-in
    const searchParams = new URLSearchParams(location.search);
    const hasCode = searchParams.has('code');
    const hasError = searchParams.has('error');

    // Handle successful Auth0 callback with authorization code
    if (hasCode) {
      StandaloneAuth.client
        .handleRedirectCallback()
        .then(({ appState }) => {
          sessionStorage.removeItem(SESSION_STORAGE_AUTH_RETURN_TO);
          const destination = appState?.returnTo ?? '/';
          navigate(destination, { replace: true });
        })
        .catch((error) => {
          sessionStorage.removeItem(SESSION_STORAGE_AUTH_RETURN_TO);
          // eslint-disable-next-line no-console
          console.error('Error handling Auth0 callback on /sign-in:', error);
          navigate('/', { replace: true });
        });
      return;
    }

    // Handle Auth0 error redirects (e.g., access_denied, login_required)
    // These don't have valid state/code to process, so we just navigate home
    if (hasError) {
      sessionStorage.removeItem(SESSION_STORAGE_AUTH_RETURN_TO);
      const error = searchParams.get('error');
      // eslint-disable-next-line no-console
      console.error('Auth0 authentication error:', error);
      navigate('/', { replace: true });
      return;
    }

    const returnTo = searchParams.get('returnTo');
    if (returnTo) {
      // URLSearchParams.get() already returns a decoded string; avoid double-decode
      // and URI malformed throws on values containing a raw %
      sessionStorage.setItem(SESSION_STORAGE_AUTH_RETURN_TO, returnTo);
      StandaloneAuth.client.loginWithRedirect({
        appState: { returnTo },
      });
      return;
    }

    StandaloneAuth.client.loginWithRedirect();
  }, [location.search, navigate]);

  return <LoadingIndicator />;
};
